// Shared "shell" UI for the light comic-art menu screens (Menu.js,
// LevelSelect.js) — a persistent left sidebar with the logo + nav, so both
// screens read as one connected app instead of two one-off layouts.
import { SaveManager } from '../SaveManager.js';
import { fetchTopScores, LEADERBOARD_ENABLED } from '../Leaderboard.js';

export const INK = '#141414';
export const INK_NUM = 0x141414;
export const PAPER = '#ffffff';
export const PAPER_NUM = 0xffffff;
export const MUTED = '#767676';
export const MUTED_NUM = 0x999999;

export const SIDEBAR_W = 256;

// The game canvas is a fixed 960x384 internal buffer (main.js) that CSS
// then stretches to fill `.game-container`, which can be over 1.6x that
// width on a wide screen (site.css: max-width: min(96vw, 1600px)) — on top
// of whatever the display's own devicePixelRatio adds. Phaser's Text
// objects rasterize to a texture at their literal font-size in pixels and
// default to resolution 1, so that small texture then gets the same
// stretch as everything else, reading as blur (worst on text, since flat-
// color shapes have no fine detail to lose). Rendering text at a higher
// internal resolution up front (this is what `resolution` on a Text style
// does — see Phaser's Text.js) fixes that without touching any layout.
export const TEXT_RESOLUTION = Math.max(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);

const NAV_ITEMS = [
  { key: 'start', icon: '▶', label: 'START GAME' },
  { key: 'levels', icon: '◆', label: 'LEVEL SELECT' },
  { key: 'endless', icon: '∞', label: 'ENDLESS' },
  { key: 'leaderboard', icon: '★', label: 'LEADERBOARD' },
  { key: 'controls', icon: '⌨', label: 'CONTROLS' },
  { key: 'about', icon: 'i', label: 'ABOUT' },
  { key: 'exit', icon: '⏏', label: 'EXIT' }
];

const CONTROLS_LINES = ['← →   MOVE', 'SPACE   JUMP  (hold a wall to slide)', 'J       SHOOT'];

const ABOUT_LINES = [
  'THE CITY IS WALLED. THE SIGNAL IS JAMMED.',
  '',
  'Fight through the cramped Lower Sector wastes, then',
  'ascend into the neon-lit Upper Sector towers.',
  '',
  'An unofficial fan project, built while waiting for',
  'Neo Berlin 2087 — not affiliated with, endorsed by,',
  'or sponsored by Elysium Game Studio.'
];

/**
 * Returns the id of the furthest level the player has unlocked, for the
 * sidebar's "start game" shortcut. Excludes `endless` (unlocked by default,
 * not part of the campaign order) — otherwise it'd always win as "latest".
 */
export function resolveStartLevelId(levelDefs) {
  const ids = Object.keys(levelDefs).filter((id) => !levelDefs[id].endless);
  let latest = ids[0];
  for (const id of ids) {
    if (SaveManager.isUnlocked(id)) latest = id;
  }
  return latest;
}

/**
 * Builds the persistent left sidebar (logo, nav buttons, footer) on `scene`.
 * `active` marks which nav key is highlighted as current.
 * `onNav(key)` is called for the two keys that need scene-specific handling
 * ('start', 'levels'); 'controls', 'about' and 'exit' are handled here since
 * they behave identically everywhere.
 */
export function buildSidebar(scene, { active, onNav }) {
  const { height } = scene.scale;

  scene.add.rectangle(SIDEBAR_W, height / 2, 2, height, INK_NUM, 0.12).setOrigin(0.5);

  const logo = scene.add
    .text(22, 14, '2087', {
      fontFamily: 'monospace',
      fontSize: '30px',
      fontStyle: 'bold',
      color: INK,
      resolution: TEXT_RESOLUTION
    })
    .setInteractive({ useHandCursor: true });
  logo.on('pointerdown', () => scene.scene.start('Menu'));

  scene.add.text(23, 47, 'A 2D SIDE-SCROLLING\nCYBERPUNK SHOOTER', {
    fontFamily: 'monospace',
    fontSize: '7px',
    color: MUTED,
    lineSpacing: 3,
    resolution: TEXT_RESOLUTION
  });

  scene.add.rectangle(SIDEBAR_W / 2, 74, SIDEBAR_W - 44, 1, INK_NUM, 0.25);

  // Pitch/box height trimmed further from the original 5-item sidebar (36px
  // pitch, 27px box) to fit ENDLESS + LEADERBOARD in the same vertical
  // budget without crowding the welcome box/footer below it.
  let y = 86;
  const pitch = 29;
  for (const item of NAV_ITEMS) {
    const isActive = item.key === active;
    const box = scene.add
      .rectangle(22, y, SIDEBAR_W - 44, 22, isActive ? INK_NUM : PAPER_NUM, isActive ? 1 : 0)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, INK_NUM)
      .setInteractive({ useHandCursor: true });
    scene.add
      .text(34, y, item.icon ? `${item.icon}  ${item.label}` : item.label, {
        fontFamily: 'monospace',
        fontSize: '10px',
        fontStyle: 'bold',
        color: isActive ? PAPER : INK,
        resolution: TEXT_RESOLUTION
      })
      .setOrigin(0, 0.5);
    box.on('pointerover', () => {
      if (!isActive) box.setFillStyle(INK_NUM, 0.07);
    });
    box.on('pointerout', () => {
      if (!isActive) box.setFillStyle(PAPER_NUM, 0);
    });
    box.on('pointerdown', () => _handleNav(scene, item.key, onNav));
    y += pitch;
  }

  scene.add
    .rectangle(22, y + 4, SIDEBAR_W - 44, 62, PAPER_NUM, 1)
    .setOrigin(0, 0)
    .setStrokeStyle(1, INK_NUM);
  scene.add.text(32, y + 13, '☠  WELCOME TO 2087', {
    fontFamily: 'monospace',
    fontSize: '8px',
    fontStyle: 'bold',
    color: INK,
    resolution: TEXT_RESOLUTION
  });
  scene.add.text(32, y + 28, 'Megacorporations rule the world.\nFight. Explore. Survive.', {
    fontFamily: 'monospace',
    fontSize: '7px',
    color: MUTED,
    lineSpacing: 3,
    resolution: TEXT_RESOLUTION
  });

  scene.add.text(22, height - 14, '© 2087 — ALL RIGHTS RESERVED', {
    fontFamily: 'monospace',
    fontSize: '6px',
    color: MUTED,
    resolution: TEXT_RESOLUTION
  });
}

function _handleNav(scene, key, onNav) {
  if (key === 'controls') {
    showOverlay(scene, 'CONTROLS', CONTROLS_LINES);
    return;
  }
  if (key === 'about') {
    showOverlay(scene, 'ABOUT 2087', ABOUT_LINES);
    return;
  }
  if (key === 'exit') {
    document.querySelector('.site-header')?.scrollIntoView({ behavior: 'smooth' });
    return;
  }
  if (key === 'endless') {
    // Same everywhere it's clicked from, so it's handled centrally here
    // rather than duplicated in every scene's onNav callback.
    scene.scene.start('Game', { levelId: 'endless' });
    return;
  }
  if (key === 'leaderboard') {
    showLeaderboardOverlay(scene);
    return;
  }
  onNav(key);
}

/** A simple centered modal — used for the CONTROLS / ABOUT nav entries, which have no dedicated scene. */
export function showOverlay(scene, title, lines) {
  const { width, height } = scene.scale;

  const backdrop = scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.55).setInteractive();
  const panelW = Math.min(560, width - 60);
  const panelH = Math.min(200, height - 40);
  const panel = scene.add
    .rectangle(width / 2, height / 2, panelW, panelH, PAPER_NUM, 1)
    .setStrokeStyle(2, INK_NUM)
    .setInteractive();
  const titleText = scene.add
    .text(width / 2, height / 2 - panelH / 2 + 18, title, {
      fontFamily: 'monospace',
      fontSize: '13px',
      fontStyle: 'bold',
      color: INK,
      resolution: TEXT_RESOLUTION
    })
    .setOrigin(0.5);
  const body = scene.add
    .text(width / 2, height / 2 - panelH / 2 + 42, lines.join('\n'), {
      fontFamily: 'monospace',
      fontSize: '9px',
      color: INK,
      align: 'center',
      lineSpacing: 6,
      resolution: TEXT_RESOLUTION
    })
    .setOrigin(0.5, 0);
  const closeHint = scene.add
    .text(width / 2, height / 2 + panelH / 2 - 12, 'CLICK TO CLOSE', {
      fontFamily: 'monospace',
      fontSize: '7px',
      color: MUTED,
      resolution: TEXT_RESOLUTION
    })
    .setOrigin(0.5);

  const group = [backdrop, panel, titleText, body, closeHint];
  const close = () => group.forEach((o) => o.destroy());
  backdrop.on('pointerdown', close);
  panel.on('pointerdown', close);
}

// High, fixed depth so this reliably draws above whatever else is on
// screen — Menu/LevelSelect have nothing else layered, but Game.js's death
// screen (where this is also opened from) uses DEPTHS.UI (100) + a handful,
// which this needs to sit above regardless of caller.
const LEADERBOARD_DEPTH = 500;

/** Fetches and displays the top 10 global endless scores. Shared by the sidebar's LEADERBOARD nav item and Game.js's endless death screen. */
export async function showLeaderboardOverlay(scene) {
  const { width, height } = scene.scale;

  const backdrop = scene.add
    .rectangle(width / 2, height / 2, width, height, 0x000000, 0.7)
    .setDepth(LEADERBOARD_DEPTH)
    .setInteractive();
  const panel = scene.add
    .rectangle(width / 2, height / 2, 260, 260, PAPER_NUM, 1)
    .setStrokeStyle(2, INK_NUM)
    .setDepth(LEADERBOARD_DEPTH + 1)
    .setInteractive();
  const title = scene.add
    .text(width / 2, height / 2 - 112, '★ GLOBAL LEADERBOARD', {
      fontFamily: 'monospace',
      fontSize: '11px',
      fontStyle: 'bold',
      color: INK,
      resolution: TEXT_RESOLUTION
    })
    .setOrigin(0.5)
    .setDepth(LEADERBOARD_DEPTH + 2);
  const body = scene.add
    .text(width / 2, height / 2 - 88, 'LOADING…', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: MUTED,
      align: 'center',
      lineSpacing: 6,
      resolution: TEXT_RESOLUTION
    })
    .setOrigin(0.5, 0)
    .setDepth(LEADERBOARD_DEPTH + 2);
  const closeHint = scene.add
    .text(width / 2, height / 2 + 112, 'CLICK TO CLOSE', {
      fontFamily: 'monospace',
      fontSize: '7px',
      color: MUTED,
      resolution: TEXT_RESOLUTION
    })
    .setOrigin(0.5)
    .setDepth(LEADERBOARD_DEPTH + 2);

  const group = [backdrop, panel, title, body, closeHint];
  const close = () => group.forEach((o) => o.destroy());
  backdrop.on('pointerdown', close);
  panel.on('pointerdown', close);

  const scores = LEADERBOARD_ENABLED ? await fetchTopScores(10) : null;
  if (!body.active) return; // closed before the fetch resolved
  if (scores === null) {
    body.setText('LEADERBOARD NOT\nCONFIGURED YET');
  } else if (scores.length === 0) {
    body.setText('NO SCORES YET —\nBE THE FIRST');
  } else {
    body.setText(scores.map((s, i) => `${String(i + 1).padStart(2, '0')}. ${s.initials}   ${s.score}`).join('\n'));
  }
}
